/**
 * Mermaid, loaded on demand from the same pinned, integrity-checked URL the old
 * doc.html used.
 *
 * The posture is deliberately unchanged from BUG-006:
 *
 *   * one pinned version, never a range;
 *   * Subresource Integrity, so a compromised CDN cannot execute on our origin;
 *   * securityLevel 'strict', set explicitly rather than inherited from the
 *     default -- this library is the ONLY sanitiser between agent-written
 *     diagram text and the SVG that ends up in the DOM;
 *   * no inline <script>, so the CSP still needs no 'unsafe-inline'.
 *
 * What changed is only *when* it loads. The old page put the tag in doc.html,
 * so every viewer paid for ~1MB of parser whether or not the document had a
 * diagram in it. Now a `<script integrity=...>` element is appended the first
 * time a mermaid code block is actually rendered. SRI applies to dynamically
 * inserted scripts exactly as it does to parsed ones, and
 * `script-src 'self' https://cdn.jsdelivr.net` already allows the origin, so
 * the policy is byte-for-byte the one that is deployed today.
 *
 * Bundling mermaid locally instead was considered: it would remove the third
 * party entirely and let jsdelivr drop out of the CSP. It is not done here
 * because it swaps a reviewed, pinned, integrity-checked artefact for whatever
 * the lockfile resolves to at build time, and that is a decision to take
 * deliberately rather than as a side effect of a framework change.
 */

const SRC = 'https://cdn.jsdelivr.net/npm/mermaid@11.12.0/dist/mermaid.min.js'
const INTEGRITY = 'sha384-o+g/BxPwhi0C3RK7oQBxQuNimeafQ3GE/ST4iT2BxVI4Wzt60SH4pq9iXVYujjaS'

type Mermaid = {
  initialize(config: Record<string, unknown>): void
  run(options: { nodes: Element[] }): Promise<void>
}

declare global {
  interface Window {
    mermaid?: Mermaid
  }
}

let loading: Promise<Mermaid> | null = null

/**
 * Diagram colours taken from the same tokens as the rest of the page, read off
 * the live computed style so the light and dark values in tokens.css remain the
 * single source of truth. Mermaid needs literal colours, not var() references.
 */
function themeVariables(): Record<string, string> {
  const css = getComputedStyle(document.documentElement)
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback
  const paper = v('--paper', '#faf9f5')
  const ink = v('--ink', '#211d18')
  const plinth = v('--color-plinth', '#f1ece4')
  const soft = v('--color-ink-soft', '#6e665c')
  const red = v('--color-red-deep', '#c01030')
  return {
    background: paper,
    primaryColor: plinth,
    primaryTextColor: ink,
    primaryBorderColor: soft,
    secondaryColor: paper,
    tertiaryColor: plinth,
    lineColor: soft,
    textColor: ink,
    mainBkg: plinth,
    nodeBorder: soft,
    clusterBkg: paper,
    clusterBorder: soft,
    titleColor: ink,
    edgeLabelBackground: paper,
    actorBkg: plinth,
    actorBorder: soft,
    actorTextColor: ink,
    noteBkgColor: plinth,
    noteTextColor: ink,
    noteBorderColor: soft,
    errorBkgColor: paper,
    errorTextColor: red,
    fontFamily: v('--font-sans', 'ui-sans-serif, system-ui, sans-serif'),
  }
}

export function loadMermaid(): Promise<Mermaid> {
  if (window.mermaid) return Promise.resolve(window.mermaid)
  if (loading) return loading

  loading = new Promise<Mermaid>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SRC
    script.integrity = INTEGRITY
    script.crossOrigin = 'anonymous'
    script.async = true
    script.addEventListener('load', () => {
      const mermaid = window.mermaid
      if (!mermaid) {
        reject(new Error('mermaid loaded but did not register itself'))
        return
      }
      // Follows the APP's theme, not the operating system's. The palette has
      // no OS dark mode -- unomena.com has none -- so asking the OS here put
      // dark diagrams on a light page for anyone with a dark desktop.
      const dark = document.documentElement.dataset.theme === 'dark'
      mermaid.initialize({
        startOnLoad: false,
        // Explicit rather than relying on the default: this is the only
        // sanitiser between agent-written diagram text and injected SVG.
        // BUG-006.
        securityLevel: 'strict',
        theme: 'base',
        themeVariables: themeVariables(),
        darkMode: dark,
      })
      resolve(mermaid)
    })
    // A failed integrity check fires `error`, same as a network failure. The
    // caller falls back to showing the diagram source as a code block.
    script.addEventListener('error', () => {
      loading = null
      script.remove()
      reject(new Error('mermaid could not be loaded'))
    })
    document.head.append(script)
  })

  return loading
}
